/**
 * Splits a given string into a brand name and a store name based on the first space character.
 *
 * This utility function is designed to separate a single string into two parts: the portion
 * before the first space (considered the brand name) and the portion after the first space
 * (considered the store name). If the input string does not contain a space, the entire string
 * will be treated as the brand name, and the store name will be an empty string.
 *
 * @param name - The input string representing a brand and store, separated by a space.
 *
 * @returns A tuple containing two strings:
 * - The first element is the brand name (portion before the first space).
 * - The second element is the store name (portion after the first space). If no space exists,
 *   this will be an empty string.
 *
 * @example
 * ```typescript
 * const [brand, store] = splitBrandAndStore("SoCoo'c Rennes");
 * console.log(brand); // "Socoo'c"
 * console.log(store); // "Rennes"
 *
 * const [brandOnly, emptyStore] = splitBrandAndStore("SoCoo'c");
 * console.log(brandOnly); // "SoCoo'c"
 * console.log(emptyStore); // ""
 * ```
 */
export const splitBrandAndStore = (name: string): [string, string] => {
  // Find the index of the first space in the name string.
  const firstSpaceIndex = name.indexOf(' ');

  // If no space is found, we assume the whole string is the brand name.
  if (firstSpaceIndex === -1) {
    return [name, ''];
  }

  // Return a tuple with the brand (part before the space) and the store name (part after).
  return [name.substring(0, firstSpaceIndex), name.substring(firstSpaceIndex + 1)];
};
